import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "col-lg-8 mx-auto" }
const _hoisted_2 = { class: "text-center" }
const _hoisted_3 = { class: "mb-4" }
const _hoisted_4 = { class: "mb-4" }
const _hoisted_5 = {
  key: 0,
  class: "mb-4"
}
const _hoisted_6 = {
  key: 2,
  class: "mb-4"
}
const _hoisted_7 = { class: "row pt-4 mb-4 mx-1" }

import { ref, watch, computed, nextTick, onMounted } from "vue";
import WorkflowNavigation from "@/guiide/components/common/WorkflowNavigation.vue";
import HelpText from "@/guiide/ts/helpText";
import Help from "@/guiide/components/common/Help.vue";
import SubSection from "@/guiide/components/common/SubSection.vue";
import OptionsQuestion from "@/guiide/components/common/OptionsQuestion.vue";
import IncomeGuidance from "./wants/IncomeGuidance.vue";
import InitialIncomeSetup from "./wants/InitialIncomeSetup.vue";
import AnnualExpenses from "./wants/AnnualExpenses.vue";
import BadResponseViewer from "@/shared/components/BadResponseViewer.vue";
import ContactUs from "./common/ContactUs.vue";
import PageNav from "@/guiide/components/common/PageNav.vue";
import { Retiree } from "../ts/retiree";
import { app } from "../ts/app";
import ResponseTracker from "@/shared/ts/responseTracker";
import { MessageResponse } from "../ts/response/messageResponse";
import { workflow } from "../ts/workflow/workflow";
import View from "../ts/view";
import { createModernIncomeArray } from "../ts/wants/wantsUtilities";
import { SuggestedAmounts } from "../ts/wants/suggestedAmounts";
import { yesNoOptions, mapBooleanToYesNo, mapYesNoToBoolean, getYesNoWidth } from "../ts/utilities";
import { useStore } from "vuex";
import { InitialIncomeProfile } from "../ts/wants/initialIncomeProfile";
import Constants from "../ts/constants";
import { IncomeGuidanceAnswers } from "../ts/wants/incomeGuidanceAnswers";
import Lifestyle from "../ts/lifestyle";
import { formatter } from "@/shared/ts/formatter";
import Monitoring from "../ts/monitoring/monitoring";
import { useUIContent } from "../ts/configuration/useUIContent";


export default /*@__PURE__*/_defineComponent({
  __name: 'Requirements',
  setup(__props) {

const store = useStore();
const { text, templateHtmlArray } = useUIContent();

const increaseDurationOptions = [Constants.InflationDurationForLife, "75"];

const suggestedAmountOnPLSADate = ref(0);
const suggestedAmountAtRetirement = ref(0);
const showExpensesUpgradeAlert = ref(false);
const taxFreeLumpSumModel = ref("Yes");
const increaseIncomeWithInflationModel = ref("Yes");
const increaseIncomeWithInflationDurationModel = ref(Constants.InflationDurationForLife);

const incomeGuidance = ref(null);
const initialIncomeSetup = ref(null);
const annualExpenses = ref(null);
const badResponseViewer = ref(null);
const nextLink = ref("");

const increaseWithInflation = computed<boolean>(() => mapYesNoToBoolean(increaseIncomeWithInflationModel.value));
const inflationAgeLimit = computed<number>(() => {
  const lifeAsAnAge = 200;

  if (!store.state.configuration.requirementsExpenseEditPriviledges) {
    return lifeAsAnAge;
  }

  return increaseIncomeWithInflationDurationModel.value == Constants.InflationDurationForLife ? lifeAsAnAge : 75;
});

onMounted(async() => {
  const nextWorkflowStep = await workflow.getNextWorkflowStep(View.Requirements);
  nextLink.value = nextWorkflowStep.resource;
});

watch(() => store.state.retiree, (retiree: Retiree) => {
  if (retiree == null) {
    return;
  }

  taxFreeLumpSumModel.value = mapBooleanToYesNo(
    retiree.requirements.takingTaxFreeAsLumpSum
  );

  increaseIncomeWithInflationModel.value = mapBooleanToYesNo(
    retiree.requirements.incomeIncreasesWithInflation
  );

  increaseIncomeWithInflationDurationModel.value = retiree.requirements.incomeIncreasesWithInflationDuration;

  const userHasAlreadyDefinedAmount = retiree.requirements.annualAfterTaxIncome != 0;
  const userHasNotDefinedModernExpenses = retiree.requirements.income.length == 0;

  if (!inNewUserSetupMode.value && userHasAlreadyDefinedAmount && userHasNotDefinedModernExpenses) {
    const totalPlanYears = retiree.lifeExpectancy;

    const annualBaseIncome = retiree.requirements.annualAfterTaxIncome;
    const extraFirstFiveYearsIncome = retiree.requirements.extraIncomeFirstNYears;
    const extraIncomeYears = 5;
    const defaultIncomeArray = createModernIncomeArray(
      annualBaseIncome,
      extraFirstFiveYearsIncome,
      extraIncomeYears,
      totalPlanYears,
      Constants.IncomeStepSize);

    nextTick(() => {
      annualExpenses.value.setIncome(defaultIncomeArray);
      const onlyShowGuiideUsersTheUpgradeMessage = store.state.configuration.requirementsExpenseEditPriviledges;
      showExpensesUpgradeAlert.value = onlyShowGuiideUsersTheUpgradeMessage;
    });

  }
});

const taxFreeCashQuestionText = computed<string>(() => {
  return store.state.retiree?.alreadyDrawingPension
    ? "When you started taking your defined contribution pension pots, did you take 25% as tax free cash straight away?"
    : "Do you want to take 25% of your defined contribution pension pots as a tax free lump sum at retirement?";
});

const taxFreeCashHelpText = computed<Array<string>>(() => {
  const taxFreeCashLimit = formatter.formatCurrency(Constants.TaxFreeCashLimit);
  return templateHtmlArray("wants.tax-free-cash.help.body", { taxFreeCashLimit });
});

const inNewUserSetupMode = computed<boolean>(() => {
  const retiree = store.state.retiree;

  if (retiree == null) {
    return false;
  }

  return !retiree.isRegistered;
});

const showIncomeGuiidance = computed<boolean>(() => {
  if (inNewUserSetupMode.value) {
    return true;
  }

  const retiree = store.state.retiree;
  if (retiree == null) {
    return true;
  }

  return !retiree.alreadyDrawingPension;
});

const showIncreasesDuration = computed<boolean>(() => {
  return store.state.configuration.requirementsExpenseEditPriviledges;
});

const incomePlanIntroText = function () {
  return store.state.configuration.requirementsExpenseEditPriviledges
    ? "If you want to get really detailed, you can change the income in any year below by pressing edit."
    : "You can see the income you plan to receive in each year of your retirement below.";
}

function onIncomeGuidanceChanged(guidance: SuggestedAmounts) {
  suggestedAmountOnPLSADate.value = guidance.suggestedAmountOnPLSADate;
  suggestedAmountAtRetirement.value = guidance.suggestedAmountAtRetirement;
}

async function onPopulateIncomePlan(incomeDetail: InitialIncomeProfile) {
  const guidanceAnswers = getIncomeGuidanceAnswers();
  Monitoring.trackEvent('generateIncomePlan', {
    retirementType: guidanceAnswers.lifestyle,
    afterTaxIncome: incomeDetail.baseIncome,
    extraIncome: incomeDetail.extraIncome,
    extraIncomeYears: incomeDetail.extraIncomeYears,
  });

  const retiree: Retiree = store.state.retiree;
  const totalPlanYears = retiree.lifeExpectancy;

  const defaultIncomeArray = createModernIncomeArray(
    incomeDetail.baseIncome,
    incomeDetail.extraIncome,
    incomeDetail.extraIncomeYears,
    totalPlanYears,
    Constants.IncomeStepSize);

  await annualExpenses.value.setIncome(defaultIncomeArray);
}

async function onNextClicked() {
  badResponseViewer.value.clear();

  const takingDCTaxFreeAsLumpSum = mapYesNoToBoolean(taxFreeLumpSumModel.value);
  const increaseIncomeWithInflation = mapYesNoToBoolean(increaseIncomeWithInflationModel.value);
  const guidanceAnswers = getIncomeGuidanceAnswers();
  const oldStyleIncome: { baseIncome: number, extraIncome: number } = initialIncomeSetup.value.getIncome();
  const income = annualExpenses.value.getIncome();

  const newUserHasntPopulatedPlan = inNewUserSetupMode.value && income.every(i => i == 0);
  if (newUserHasntPopulatedPlan) {
    badResponseViewer.value.update(false, "Please click the 'Generate your income plan' button to create your income plan.");
    return;
  }

  const responseTracker: ResponseTracker<MessageResponse> = await app.updateRequirements(
    takingDCTaxFreeAsLumpSum,
    guidanceAnswers.livesInLondon,
    guidanceAnswers.supportingPartner,
    guidanceAnswers.lifestyle,
    oldStyleIncome.baseIncome,
    oldStyleIncome.extraIncome,
    increaseIncomeWithInflation,
    increaseIncomeWithInflationDurationModel.value,
    income
  );

  if (responseTracker.isOk) {
    await workflow.goToNextWorkflowStep();
  } else {
    if (responseTracker.trackerWillHandleFailure) {
      responseTracker.handleFailure();
    } else {
      badResponseViewer.value.update(
        responseTracker.isOk,
        responseTracker.message
      );
    }
  }
}

function getIncomeGuidanceAnswers(): IncomeGuidanceAnswers {
  const userAlreadyRetired = !showIncomeGuiidance.value;
  if (userAlreadyRetired) {
    const retiree: Retiree | null = store.state.retiree;
    if (retiree == null) {
      return { livesInLondon: false, supportingPartner: false, lifestyle: Lifestyle.Moderate };
    }
    return {
      livesInLondon: retiree.requirements.livesInLondon,
      supportingPartner: retiree.requirements.isSupportingPartner,
      lifestyle: retiree.requirements.targetLifestyle
    };
  }

  return incomeGuidance.value.getAnswers();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createVNode(WorkflowNavigation, { workflowStage: "Wants" }),
      _cache[3] || (_cache[3] = _createElementVNode("div", { class: "col-lg-10 mx-auto" }, [
        _createElementVNode("h2", { class: "skin-page-heading" }, "What income do you want in retirement?"),
        _createElementVNode("p", null, " Answer the questions below to get a clearer picture of your retirement goals. You can change these answers and see the effect as many times as you want. ")
      ], -1))
    ]),
    _createVNode(SubSection, {
      title: "Your retirement income",
      showHelp: false
    }, {
      default: _withCtx(() => [
        _createElementVNode("h3", null, _toDisplayString(_unref(text)('wants.tax-free-cash.title')), 1),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(OptionsQuestion, {
            name: "taxFreeLumpSum",
            question: taxFreeCashQuestionText.value,
            options: _unref(yesNoOptions),
            selected: taxFreeLumpSumModel.value,
            "onUpdate:selected": _cache[0] || (_cache[0] = ($event: any) => ((taxFreeLumpSumModel).value = $event)),
            width: _unref(getYesNoWidth)()
          }, {
            default: _withCtx(() => [
              _createVNode(Help, {
                title: "Tax free cash",
                text: taxFreeCashHelpText.value
              }, null, 8, ["text"])
            ]),
            _: 1
          }, 8, ["question", "options", "selected", "width"])
        ]),
        _cache[5] || (_cache[5] = _createElementVNode("h3", null, "Increases after you retire", -1)),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(OptionsQuestion, {
            name: "increaseIncomeWithInflation",
            question: "Do you want your income to increase after you retire?",
            options: _unref(yesNoOptions),
            selected: increaseIncomeWithInflationModel.value,
            "onUpdate:selected": _cache[1] || (_cache[1] = ($event: any) => ((increaseIncomeWithInflationModel).value = $event)),
            width: _unref(getYesNoWidth)()
          }, {
            default: _withCtx(() => [
              _createVNode(Help, {
                title: "Increases",
                text: _unref(HelpText).ReqmntInflation()
              }, null, 8, ["text"])
            ]),
            _: 1
          }, 8, ["options", "selected", "width"])
        ]),
        (showIncreasesDuration.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(OptionsQuestion, {
                name: "increaseIncomeWithInflationDuration",
                question: "How long do you want it to increase for? Your whole life, or just until 75?",
                options: increaseDurationOptions,
                selected: increaseIncomeWithInflationDurationModel.value,
                "onUpdate:selected": _cache[2] || (_cache[2] = ($event: any) => ((increaseIncomeWithInflationDurationModel).value = $event)),
                width: _unref(getYesNoWidth)(),
                disabled: increaseIncomeWithInflationModel.value == 'No'
              }, {
                default: _withCtx(() => [
                  _createVNode(Help, {
                    title: "Increases duration",
                    text: _unref(HelpText).ReqmntInflationDuration()
                  }, null, 8, ["text"])
                ]),
                _: 1
              }, 8, ["selected", "width", "disabled"])
            ]))
          : _createCommentVNode("", true),
        (showIncomeGuiidance.value)
          ? (_openBlock(), _createBlock(IncomeGuidance, {
              key: 1,
              ref_key: "incomeGuidance",
              ref: incomeGuidance,
              "show-description": !inNewUserSetupMode.value,
              "display-content": inNewUserSetupMode.value,
              onIncomeGuidanceChanged: onIncomeGuidanceChanged
            }, null, 8, ["show-description", "display-content"]))
          : _createCommentVNode("", true),
        _createVNode(InitialIncomeSetup, {
          ref_key: "initialIncomeSetup",
          ref: initialIncomeSetup,
          visible: inNewUserSetupMode.value,
          suggestedAmountOnPLSADate: suggestedAmountOnPLSADate.value,
          baseIncome: suggestedAmountAtRetirement.value,
          extraInitialIncome: 0,
          onPopulateIncomePlan: onPopulateIncomePlan
        }, null, 8, ["visible", "suggestedAmountOnPLSADate", "baseIncome"]),
        _cache[6] || (_cache[6] = _createElementVNode("h3", null, "Your income plan", -1)),
        (showExpensesUpgradeAlert.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, _cache[4] || (_cache[4] = [
              _createElementVNode("div", {
                class: "alert alert-success rounded",
                role: "alert"
              }, " We've upgraded this side of Guiide. You can now specify the income you want for each year in your retirement plan individually. Please check that you are happy with the incomes that have been setup from your previous specification and make any alterations you would like to build into your plan. ", -1)
            ])))
          : _createCommentVNode("", true),
        _createElementVNode("p", null, _toDisplayString(incomePlanIntroText()), 1),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(AnnualExpenses, {
            ref_key: "annualExpenses",
            ref: annualExpenses,
            "is-new-user": inNewUserSetupMode.value,
            "inflation-increase-enabled": increaseWithInflation.value,
            inflationAgeLimit: inflationAgeLimit.value
          }, null, 8, ["is-new-user", "inflation-increase-enabled", "inflationAgeLimit"])
        ]),
        _createElementVNode("p", null, _toDisplayString(_unref(text)('wants.shortfall.description')), 1),
        _createVNode(BadResponseViewer, {
          ref_key: "badResponseViewer",
          ref: badResponseViewer
        }, null, 512)
      ]),
      _: 1
    }),
    _createVNode(PageNav, {
      backLink: "lifeexpectancy",
      onNextClicked: onNextClicked,
      "parent-will-handle-next": true,
      "next-link": nextLink.value
    }, null, 8, ["next-link"]),
    _createVNode(ContactUs, { class: "mt-5" })
  ]))
}
}

})